import {
    ChangeDetectionStrategy,
    Component,
    Input,
    Self,
    OnInit,
    ChangeDetectorRef,
    input,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, NgControl, Validators } from '@angular/forms';
import { debounceTime, tap } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ScheduleItemData } from '@bazis/form/models/form-element.types';
import moment from 'moment';
import { TranslocoService } from '@jsverse/transloco';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { BazisFormService } from '@bazis/form/services/form.service';
import {
    API_DATETIME_FORMAT,
    API_DATETIME_FORMAT_Z,
    BazisConfigurationService,
} from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-schedule',
    templateUrl: './input-schedule.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisInputScheduleComponent extends BazisControlValueAccessor implements OnInit {
    @Input()
    addButtonTitleKey = '';

    readonly anchorRoot = input<string>('');

    dateFormat: string = API_DATETIME_FORMAT;

    disabled = false;

    scheduleForm = new FormGroup({ schedule: new FormArray([]) });

    schedule = this.scheduleForm.controls.schedule as FormArray;

    daysOfWeek = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'].map((day) => {
        return {
            id: day,
            nameKey: `weekDays.short.${day}`,
        };
    });

    previousSchedule = []; // to keep previous schedule value

    valueChanges$ = this.scheduleForm.valueChanges.pipe(
        debounceTime(500),
        tap((value) => {
            const schedule = {};
            // GOAL: if we add day of week that exists in another schedule row, we must remove it from old row

            // detect index of row that was changed
            const changedIndex = value.schedule.findIndex(
                (v, index) =>
                    v &&
                    this.previousSchedule[index] &&
                    JSON.stringify(v) !== JSON.stringify(this.previousSchedule[index]),
            );
            const changedItem = value.schedule[changedIndex];

            // if we have changedItem and selected days inside it,
            // we must check another items and remove these days from their values.
            if (changedIndex > -1 && changedItem.days.length > 0) {
                value.schedule.forEach((scheduleItem, index) => {
                    if (index === changedIndex) return;
                    const days = scheduleItem.days.reduce((acc, day) => {
                        if (changedItem.days.indexOf(day) === -1) {
                            acc.push(day);
                        }
                        return acc;
                    }, []);

                    if (days.length !== scheduleItem.days.length) {
                        (this.schedule.at(index) as FormGroup).controls.days.setValue(days);
                    }
                });
            }

            // save current value as previous - full copy
            this.previousSchedule = JSON.parse(JSON.stringify(this.schedule.value));

            // generate new schedule to save it in api
            value.schedule.forEach((scheduleItem) => {
                const time = {
                    rest: {
                        to: this.ISOtoMinutes(scheduleItem.restHours[1]),
                        from: this.ISOtoMinutes(scheduleItem.restHours[0]),
                    },
                    work: {
                        to: this.ISOtoMinutes(scheduleItem.workingHours[1]),
                        from: this.ISOtoMinutes(scheduleItem.workingHours[0]),
                    },
                };
                scheduleItem.days.forEach((day) => {
                    schedule[day] = time;
                });
            });
            this.onChange(Object.keys(schedule).length ? schedule : null);
        }),
    );

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        configurationService: BazisConfigurationService,
        private translateService: TranslocoService,
    ) {
        super(ngControl, cdr, configurationService);
        ngControl.valueAccessor = this;
    }

    ngOnInit() {
        if (this.ngControl.control instanceof EntityFormControl) {
            BazisFormService.getPropertiesFromConfig(this, this.ngControl.control.$config);
            this.ngControl.control.$config.componentValidatorFns = [this.validate.bind(this)];
        }
        this.ngControl.control.setValidators([this.validate.bind(this)]);
        this.ngControl.control.updateValueAndValidity();

        this.scheduleForm.updateValueAndValidity();
    }

    validate({ value }: FormControl) {
        let componentError;

        this.scheduleForm.controls.schedule.controls.forEach((scheduleLine, index) => {
            if (scheduleLine.errors?.scheduleDaysEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-days-' + index,
                    titleKey: 'form.title.schedule.days',
                };
            }
            if (scheduleLine.errors?.scheduleWorkingHoursEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-work-' + index,
                    titleKey: 'form.title.schedule.work',
                };
            }
            if (scheduleLine.errors?.scheduleRestHoursEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-rest-' + index,
                    titleKey: 'form.title.schedule.rest',
                };
            }
        });

        return this.scheduleForm.status === 'VALID'
            ? null
            : { componentError: componentError ? componentError : true };
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.scheduleForm.disable();
        } else {
            this.scheduleForm.enable();
        }
    }

    writeValue(value): void {
        this.schedule.clear({ emitEvent: false });

        if (!value || !Object.keys(value).length) {
            this.init();
            return;
        }
        const groups = [];
        Object.keys(value).forEach((day) => {
            const group = groups.find(
                (existedGroup) => JSON.stringify(existedGroup.time) === JSON.stringify(value[day]),
            );
            if (group) {
                group.days.push(day);
            } else {
                groups.push({
                    days: [day],
                    time: value[day],
                });
            }
        });
        groups.forEach((groupItem) => {
            this.schedule.push(this.generateScheduleItem(groupItem.time, groupItem.days));
        });
        this.previousSchedule = JSON.parse(JSON.stringify(this.schedule.value));
    }

    init() {
        this.schedule.push(this.generateScheduleItem());
        setTimeout(() => {
            this.scheduleForm.updateValueAndValidity();
        });
    }

    generateScheduleItem(data: ScheduleItemData = null, days = null) {
        return new FormGroup(
            {
                days: new FormControl(days || [], [Validators.required]),
                workingHours: new FormControl(this.numberIntervalToStringInterval(data?.work)),
                restHours: new FormControl(this.numberIntervalToStringInterval(data?.rest)),
            },
            [this.validateScheduleItem],
        );
    }

    validateScheduleItem(item) {
        const err = { wrongScheduleRow: true };

        if (!item.controls.days.value.length) return { scheduleDaysEmpty: true };
        if (!item.controls.workingHours.value) return { scheduleWorkingHoursEmpty: true };
        if (!item.controls.restHours.value) return { scheduleRestHoursEmpty: true };

        if (
            !item.controls.days.valid ||
            !item.controls.workingHours.valid ||
            !item.controls.restHours.valid
        ) {
            return err;
        }

        // force to the same year, month, date
        const rest = item.value.restHours;
        const work = item.value.workingHours;
        const workFrom = moment.utc(work[0]).year(1970).month(0).date(1);
        const workTo = moment.utc(work[1]).year(1970).month(0).date(1);
        const restFrom = moment.utc(rest[0]).year(1970).month(0).date(1);
        const restTo = moment.utc(rest[1]).year(1970).month(0).date(1);

        return item.value.restHours[1] &&
            item.value.restHours[0] &&
            (restFrom < workFrom || restTo > workTo)
            ? err
            : null;
    }

    numberIntervalToStringInterval(date) {
        return date
            ? [
                  date.from === 0 || date.from
                      ? `1970-01-01T${date.from / 60}:${date.from % 60}`
                      : '', // *60000 convert minutes to milliseconds
                  date.to === 0 || date.to ? `1970-01-01T${date.to / 60}:${date.to % 60}` : '',
              ]
            : [];
    }

    addScheduleGroup() {
        this.schedule.push(this.generateScheduleItem());
    }

    removeSchedule(i: number) {
        this.schedule.removeAt(i);
    }

    ISOtoMinutes(isoString) {
        if (!isoString) return null;

        const date = moment(isoString);
        if (!moment.utc(isoString).isValid()) return null;

        return date.hours() * 60 + date.minutes();
    }
}
